import React from 'react';
import { styled } from '@compiled/react';
import { CSSTransition } from 'react-transition-group';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import CrossIcon from '@atlaskit/icon/core/migration/close--cross';
import { token } from '@atlaskit/tokens';
import { getBaseAutomationUrl } from '@atlassian/jira-automation-menu/src/common/utils.tsx';
import type { ProjectType } from '@atlassian/jira-common-constants/src/index.tsx';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	BANNER,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { useReleaseAutomationBanner } from '../../../services/index.tsx';
import lightning from './assets/lightning.svg';
import messages from './messages.tsx';

export const buildNewRuleAutomationUrl = (projectProps: {
	projectKey: ProjectKey;
	projectType: ProjectType;
	isSimplified: boolean;
}) =>
	`${getBaseAutomationUrl(
		projectProps,
	)}#rule/new?componentFilterCriteria=RELEASE_MANAGEMENT_TRIGGERS`;

type Props = {
	isTransitionEnabled?: boolean;
};

export const ReleaseAutomationBanner = ({ isTransitionEnabled = true }: Props) => {
	const { showBanner, dismissBanner } = useReleaseAutomationBanner();
	const { data: projectContext, loading, error } = useProjectContext();
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	if (loading) {
		return null;
	}

	if (error) {
		throw error;
	}

	if (projectContext == null) {
		throw Error('Missing project context when rendering ReleaseAutomationBanner');
	}

	const { simplified: isSimplified, projectKey, projectType } = projectContext;

	const renderBannerContent = (
		<>
			<Spacer />
			<Section>
				<img width={14} height={16} src={lightning} alt="" />
				<CallToAction
					href={buildNewRuleAutomationUrl({
						isSimplified,
						projectKey,
						projectType,
					})}
					target="_blank"
					rel="noopener noreferrer"
					title={formatMessage(messages.setupReleaseAutomation)}
					// eslint-disable-next-line jira/react-no-inline-function-prop
					onClick={() => {
						fireUIAnalytics(
							createAnalyticsEvent({
								action: 'clicked',
								actionSubject: 'link',
							}),
							'newRuleAutomationLink',
						);
						dismissBanner();
					}}
				>
					{formatMessage(messages.setupReleaseAutomation)}
				</CallToAction>
				<Dismiss>
					<Button
						shouldFitContainer
						appearance="subtle"
						// eslint-disable-next-line jira/react-no-inline-function-prop
						onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'dismissButton');
							dismissBanner();
						}}
						iconBefore={<CrossIcon label={formatMessage(messages.dismiss)} LEGACY_size="small" />}
					/>
				</Dismiss>
			</Section>
		</>
	);

	return (
		<ContextualAnalyticsData sourceType={BANNER} sourceName="releaseAutomationCallToAction">
			<FireScreenAnalytics />
			{isTransitionEnabled ? (
				<Wrapper>
					<CSSTransition
						in={showBanner}
						timeout={TRANSITION_DURATION}
						classNames="release-automation-banner"
						unmountOnExit
					>
						{renderBannerContent}
					</CSSTransition>
				</Wrapper>
			) : (
				renderBannerContent
			)}
		</ContextualAnalyticsData>
	);
};

const BANNER_HEIGHT = 80;
const TRANSITION_DURATION = 400;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Section = styled.section({
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderWidth: `${gridSize * 0.25}px`,
	borderStyle: 'solid',

	borderColor: `${token('color.border')}`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${gridSize * 0.5}px`,
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.200'),
	gap: token('space.200'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CallToAction = styled.a({
	flexGrow: 1,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Dismiss = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	width: `${gridSize * 4}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spacer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 3}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.release-automation-banner-enter': {
		opacity: 0,
		overflow: 'hidden',
		maxHeight: 0,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.release-automation-banner-enter-active': {
		opacity: 1,
		overflow: 'hidden',
		maxHeight: `${BANNER_HEIGHT}px`,
		transition: `all ${TRANSITION_DURATION}ms ease-in-out`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.release-automation-banner-exit': {
		opacity: 1,
		overflow: 'hidden',
		maxHeight: `${BANNER_HEIGHT}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'.release-automation-banner-exit-active': {
		opacity: 0,
		overflow: 'hidden',
		maxHeight: '0px',
		transition: `all ${TRANSITION_DURATION}ms ease-in-out`,
	},
});
